import { useAppContext } from 'contexts/AppContext';
import React from 'react';
import { checkUploadFileStatus, getJsonFromS3 } from 'utils/files';

const API_STATUS_FETCH = 5000;
const useApiStatusCheck = (loaderType: 'pdf' | 'none' | 'page' = 'pdf') => {
  const { pdfLoading, snack, pageLoading } = useAppContext();
  const [isPolling, setIsPolling] = React.useState(false);

  const intervalId = React.useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const currentLoader =
    loaderType === 'pdf' ? pdfLoading : loaderType === 'page' ? pageLoading : null;

  let trackingId = '';

  // callback function
  let onComplete: (res?: unknown) => void;

  // onFail function
  let onFailCallBack: ((errors?: string[]) => void) | undefined;

  // for starting the interval of API calls
  const startInterval = (
    id: string,
    callBackFn: (res: unknown) => void,
    intervalTime: number = API_STATUS_FETCH,
    onFail?: (errors?: string[]) => void,
  ) => {
    currentLoader && currentLoader.show();
    setIsPolling(true);

    // setting variables
    onComplete = callBackFn;
    trackingId = id;
    onFailCallBack = onFail;

    intervalId.current = setInterval(checkStatus, intervalTime);
  };

  const stopInterval = () => {
    // stop calling API
    clearInterval(intervalId.current);
    currentLoader && currentLoader.hide();
    setIsPolling(false);
  };

  const checkStatus = async () => {
    try {
      const res = await checkUploadFileStatus(trackingId);
      const resStatus = res.data.data.task.status;
      if (resStatus === 'done') {
        const result = res.data.data.task.result;
        const s3Link = res.data.data.task.s3Link;
        if (result) {
          onComplete(result);
        } else if (s3Link) {
          getJsonFromS3(s3Link).then(async (res) => {
            const data = await res.json();
            onComplete(data);
          });
        } else {
          onComplete();
        }
        stopInterval();
      } else if (resStatus === 'failed') {
        const result = res.data.data.task.result as { msg: string; errors: string[] };
        stopInterval();
        onFailCallBack && onFailCallBack(result.errors);
        snack.show(result.msg as string, 'error');
      }
    } catch (error) {
      stopInterval();
      onFailCallBack && onFailCallBack([error as string]);
      snack.show(error as string, 'error');
    }
  };

  React.useEffect(
    () => () => {
      stopInterval();
    },
    [],
  );

  return { startCheck: startInterval, isPolling, setIsPolling };
};
export default useApiStatusCheck;
